import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Role } from '@ndvr/auth-api';

import { AuthApi } from 'API';
import { Toast } from 'widgets';

export const activeRolesQueryKey = (personOrgGUID: string) =>
    ['personOrg', personOrgGUID, 'credential', 'roles'] as const;

export const useActiveRoles = (personOrgGUID: string) =>
    useQuery({
        queryKey: activeRolesQueryKey(personOrgGUID),
        queryFn: async () => {
            const [response] = await AuthApi().getActiveRoles(personOrgGUID);
            return response ?? [];
        },
        enabled: !!personOrgGUID,
    });

export const useAssignRole = (personOrgGUID: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (role: Role) => {
            return AuthApi({ hideError: true }).assignRole(personOrgGUID, { role });
        },
        onSuccess: () => {
            Toast.success('Role added');
            queryClient.invalidateQueries({
                queryKey: ['personOrg', personOrgGUID, 'credential'],
            });
            queryClient.invalidateQueries({
                queryKey: activeRolesQueryKey(personOrgGUID),
            });
        },
        onError: (e: unknown) => {
            Toast.forError(e, 'Failed to add role');
        },
    });
};

export const useRemoveRole = (personOrgGUID: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (role: Role) => {
            return AuthApi({ hideError: true }).removeRole(personOrgGUID, role);
        },
        onSuccess: () => {
            Toast.success('Role removed');
            queryClient.invalidateQueries({
                queryKey: ['personOrg', personOrgGUID, 'credential'],
            });
            queryClient.invalidateQueries({
                queryKey: activeRolesQueryKey(personOrgGUID),
            });
        },
        onError: (e: unknown) => {
            Toast.forError(e, 'Failed to remove role');
        },
    });
};
